import React from 'react';

import { generateRandomOrderNumber } from '../mockData';
import {
  ConfiguratorContextLiveDetails,
  ConfiguratorLiveDetailsClickHandler,
  ConfiguratorLiveLayout,
  Maybe,
} from '../types';
import { isEmailValid } from '../utils/validators';

export function useLiveStep() {
  const [accountEmail, setAccountEmail] = React.useState<Maybe<string>>();
  const [accountEmailError, setAccountEmailError] = React.useState<boolean>(false);

  const [confirmEmail, setConfirmEmail] = React.useState<Maybe<string>>();
  const [confirmEmailError, setConfirmEmailError] = React.useState<boolean>(false);

  const [termsAccepted, setTermsAccepted] = React.useState<boolean>(false);

  const [layout, setLayout] = React.useState<ConfiguratorLiveLayout>('default');

  const handleReviewClick = React.useCallback<ConfiguratorLiveDetailsClickHandler>(() => {
    setLayout('review');
  }, []);

  const handleCompleteClick = React.useCallback<ConfiguratorLiveDetailsClickHandler>(() => {
    setLayout('completed');
  }, []);

  const handleBackClick = React.useCallback<ConfiguratorLiveDetailsClickHandler>(() => {
    setLayout('default');
  }, []);

  const handleAccountEmailChange = React.useCallback<React.ChangeEventHandler<HTMLInputElement>>((event) => {
    const value = event.target.value.trim();

    const isValid = isEmailValid(value);

    const name = isValid ? value : undefined;
    const error = !isValid;

    setAccountEmail(name);
    setAccountEmailError(error);
  }, []);

  const handleConfirmEmailChange = React.useCallback<React.ChangeEventHandler<HTMLInputElement>>(
    (event) => {
      const value = event.target.value.trim();

      const isValid = isEmailValid(value) && value === accountEmail;

      const name = isValid ? value : undefined;
      const error = !isValid;

      setConfirmEmail(name);
      setConfirmEmailError(error);
    },
    [accountEmail],
  );

  const handleTermsChange = React.useCallback<React.ChangeEventHandler<HTMLInputElement>>((event) => {
    const checked = event.target.checked;

    setTermsAccepted(checked);
  }, []);

  const liveDetails = React.useMemo<ConfiguratorContextLiveDetails>(
    () => ({
      toReviewClick: handleReviewClick,
      toCompleteClick: handleCompleteClick,
      onBackClick: handleBackClick,
    }),
    [handleReviewClick, handleCompleteClick, handleBackClick],
  );

  return React.useMemo(
    () => ({
      liveDetails,
      liveLayout: layout,
      liveOrderNumber: generateRandomOrderNumber(),
      accountEmail,
      accountEmailError,
      onAccountEmailChange: handleAccountEmailChange,
      confirmEmail,
      confirmEmailError,
      onConfirmEmailChange: handleConfirmEmailChange,
      termsAccepted,
      onTermsChange: handleTermsChange,
    }),
    [
      liveDetails,
      layout,
      accountEmail,
      accountEmailError,
      handleAccountEmailChange,
      confirmEmail,
      confirmEmailError,
      handleConfirmEmailChange,
      termsAccepted,
      handleTermsChange,
    ],
  );
}
